import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import ProfaseModal from '../../commons/ProfaseModal';

export type EmpresaHeaderEtiqueta = {
  id: number;
  nombre: string;
  color?: string | null;
  habilitado?: boolean | null;
};

type EmpresaHeaderEtiquetasProps = {
  empresaId: string;
  etiquetas: EmpresaHeaderEtiqueta[];
  canEdit: boolean;
  onChange: (etiquetas: EmpresaHeaderEtiqueta[]) => void;
  onError: (message: string) => void;
};

export default function EmpresaHeaderEtiquetas({
  empresaId,
  etiquetas,
  canEdit,
  onChange,
  onError,
}: EmpresaHeaderEtiquetasProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="d-flex flex-wrap justify-content-start align-items-center"
        style={{ gap: 6, minWidth: 0 }}
        aria-label="Etiquetas de la empresa"
      >
        {etiquetas.length === 0 ? (
          <span style={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.3 }}>Sin Etiquetas</span>
        ) : (
          etiquetas.map(item => (
            <span
              key={item.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                padding: '2px 8px',
                fontSize: '0.72rem',
                fontWeight: 600,
                lineHeight: 1.3,
                color: '#fff',
                background: item.color || '#ec4899',
              }}
            >
              {item.nombre}
            </span>
          ))
        )}
        <button
          type="button"
          className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
          style={{ width: 28, height: 22, padding: 0 }}
          onClick={() => setOpen(true)}
          aria-label="Ver etiquetas"
          title="Etiquetas"
        >
          <FaEye size={12} />
        </button>
      </div>
      <EmpresaEtiquetasModal
        open={open}
        empresaId={empresaId}
        assigned={etiquetas}
        canEdit={canEdit}
        onChange={onChange}
        onError={onError}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function EmpresaEtiquetasModal({
  open,
  empresaId,
  assigned,
  canEdit,
  onChange,
  onError,
  onClose,
}: {
  open: boolean;
  empresaId: string;
  assigned: EmpresaHeaderEtiqueta[];
  canEdit: boolean;
  onChange: (etiquetas: EmpresaHeaderEtiqueta[]) => void;
  onError: (message: string) => void;
  onClose: () => void;
}) {
  const [catalogo, setCatalogo] = useState<EmpresaHeaderEtiqueta[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const assignedIds = useMemo(() => new Set(assigned.map(item => item.id)), [assigned]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get<EmpresaHeaderEtiqueta[]>('api/etiquetas', {
        params: { page: 0, size: 1000, sort: 'nombre,asc' },
      })
      .then(res => {
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data : [];
        setCatalogo(
          items
            .slice()
            .sort((a, b) => (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es')),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogo([]);
          onError('No se pudieron cargar las etiquetas.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, onError]);

  const toggle = async (etiqueta: EmpresaHeaderEtiqueta, checked: boolean) => {
    if (!canEdit || savingId != null) return;
    setSavingId(etiqueta.id);
    try {
      if (checked) {
        const res = await axios.post<EmpresaHeaderEtiqueta>(
          `api/empresas/${empresaId}/etiquetas/${etiqueta.id}`,
        );
        const saved = res.data?.id != null ? res.data : etiqueta;
        onChange(assigned.some(item => item.id === saved.id) ? assigned : [...assigned, saved]);
      } else {
        await axios.delete(`api/empresas/${empresaId}/etiquetas/${etiqueta.id}`);
        onChange(assigned.filter(item => item.id !== etiqueta.id));
      }
    } catch {
      onError('No se pudo actualizar la etiqueta de la empresa.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <ProfaseModal open={open} titleId="empresa-header-etiquetas-title" size="compact" scroll>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <h2 id="empresa-header-etiquetas-title" className="profase-modal-title m-0">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Etiquetas
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <div
        role="list"
        style={{
          maxHeight: 'min(420px, 55vh)',
          overflow: 'auto',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
        }}
      >
        {loading ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: 13 }}>
            Cargando…
          </div>
        ) : catalogo.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: 13, fontStyle: 'italic' }}>
            Sin etiquetas
          </div>
        ) : (
          catalogo.map(item => {
            const checked = assignedIds.has(item.id);
            const disabled = !canEdit || savingId != null || (item.habilitado === false && !checked);
            return (
              <label
                key={item.id}
                role="listitem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--crm-border)',
                  fontSize: 13,
                  color: 'var(--crm-text-primary)',
                  cursor: disabled ? 'default' : 'pointer',
                  margin: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={e => {
                    void toggle(item, e.target.checked);
                  }}
                  aria-label={item.nombre}
                />
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 99,
                    flexShrink: 0,
                    background: item.color || '#ec4899',
                  }}
                />
                <span style={{ minWidth: 0, fontWeight: 600 }}>{item.nombre}</span>
              </label>
            );
          })
        )}
      </div>
    </ProfaseModal>
  );
}
